import { useState, useEffect, useCallback } from 'react';
import { useTenant } from '../context/TenantContext';

// Verwaltung der Abteilungen + Unter-Abteilungen eines Mandanten.
// 2 Ebenen: Top-Level + ein Sub-Level. UI = ausklappbarer Baum.
// canEdit steuert, ob Buttons angezeigt werden (admin/owner ja, member nein).
export default function DepartmentsPanel({ canEdit }) {
  const { activeTenant, tenantFetch } = useTenant();
  const [departments, setDepartments] = useState([]);
  const [members, setMembers] = useState([]); // Tenant-Mitglieder, für Member-Picker
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Inline-Editing-Zustände
  const [creatingTopName, setCreatingTopName] = useState('');
  const [creatingChildFor, setCreatingChildFor] = useState(null); // departmentId
  const [creatingChildName, setCreatingChildName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set()); // welche Abteilungen sind aufgeklappt
  const [memberDataByDept, setMemberDataByDept] = useState({}); // { deptId: [members] }
  const [memberPickerFor, setMemberPickerFor] = useState(null); // deptId, wo der Picker offen ist
  const [pickerUserId, setPickerUserId] = useState('');
  const [pickerRole, setPickerRole] = useState('member');

  // Template-Modal-States
  const [showTemplate, setShowTemplate] = useState(false);
  const [templateText, setTemplateText] = useState('');
  const [templateBusy, setTemplateBusy] = useState(false);
  const [templateMsg, setTemplateMsg] = useState('');

  const load = useCallback(async () => {
    if (!activeTenant) return;
    setLoading(true);
    try {
      const [depRes, memRes] = await Promise.all([
        tenantFetch('/api/departments'),
        tenantFetch(`/api/tenants/${activeTenant._id}/members`)
      ]);
      const depData = await depRes.json();
      const memData = await memRes.json();
      if (depRes.ok) setDepartments(depData.departments || []);
      if (memRes.ok) setMembers(memData.members || []);
    } finally {
      setLoading(false);
    }
  }, [activeTenant, tenantFetch]);

  useEffect(() => { load(); }, [load]);

  const topLevels = departments.filter(d => !d.parentId);
  const childrenOf = (id) => departments.filter(d => d.parentId === id);

  // Member-Daten für eine Abteilung lazy laden, sobald sie ausgeklappt wird
  const loadDeptMembers = async (deptId) => {
    try {
      const res = await tenantFetch(`/api/departments/${deptId}/members`);
      const data = await res.json();
      if (res.ok) setMemberDataByDept(prev => ({ ...prev, [deptId]: data.members || [] }));
    } catch { /* ignore */ }
  };

  const toggleExpand = (deptId) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(deptId)) {
        next.delete(deptId);
      } else {
        next.add(deptId);
        if (!memberDataByDept[deptId]) loadDeptMembers(deptId);
      }
      return next;
    });
  };

  const createTop = async (e) => {
    e.preventDefault();
    if (!creatingTopName.trim()) return;
    setError('');
    try {
      const res = await tenantFetch('/api/departments', {
        method: 'POST',
        body: JSON.stringify({ name: creatingTopName.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      setCreatingTopName('');
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const createChild = async (parentId) => {
    if (!creatingChildName.trim()) return;
    setError('');
    try {
      const res = await tenantFetch('/api/departments', {
        method: 'POST',
        body: JSON.stringify({ name: creatingChildName.trim(), parentId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      setCreatingChildFor(null);
      setCreatingChildName('');
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const submitRename = async (id) => {
    if (!editingName.trim()) { setEditingId(null); return; }
    try {
      const res = await tenantFetch(`/api/departments/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: editingName.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      setEditingId(null);
      setEditingName('');
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  const deleteDept = async (id, name) => {
    if (!window.confirm(`Abteilung "${name}" löschen?`)) return;
    try {
      const res = await tenantFetch(`/api/departments/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  const addMember = async (deptId) => {
    if (!pickerUserId) return;
    try {
      const res = await tenantFetch(`/api/departments/${deptId}/members`, {
        method: 'POST',
        body: JSON.stringify({ userId: pickerUserId, role: pickerRole })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      setMemberPickerFor(null);
      setPickerUserId('');
      setPickerRole('member');
      await loadDeptMembers(deptId);
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  const changeMemberRole = async (deptId, userId, role) => {
    try {
      const res = await tenantFetch(`/api/departments/${deptId}/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      await loadDeptMembers(deptId);
    } catch (err) {
      alert(err.message);
    }
  };

  const removeMember = async (deptId, userId) => {
    try {
      const res = await tenantFetch(`/api/departments/${deptId}/members/${userId}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      await loadDeptMembers(deptId);
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  // ── Template-Funktionen ────────────────────────────────────────────
  const openExport = async () => {
    setTemplateMsg('');
    setShowTemplate(true);
    try {
      const res = await tenantFetch('/api/departments/template/export');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      setTemplateText(JSON.stringify(data, null, 2));
    } catch (err) {
      setTemplateText('');
      setTemplateMsg(err.message);
    }
  };

  const openImport = () => {
    setShowTemplate(true);
    setTemplateText('');
    setTemplateMsg('Füge ein zuvor exportiertes JSON hier ein und klicke „Importieren".');
  };

  const submitImport = async () => {
    setTemplateBusy(true);
    setTemplateMsg('');
    try {
      const parsed = JSON.parse(templateText);
      const departmentsToImport = Array.isArray(parsed?.departments) ? parsed.departments : null;
      if (!departmentsToImport) throw new Error('Ungültiges Format — erwarte ein Objekt mit "departments"-Array.');
      const res = await tenantFetch('/api/departments/template/import', {
        method: 'POST',
        body: JSON.stringify({ departments: departmentsToImport })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      const created = data.created?.length || 0;
      const skipped = data.skipped?.length || 0;
      setTemplateMsg(`${created} importiert${skipped ? `, ${skipped} übersprungen (Name existiert bereits)` : ''}.`);
      await load();
    } catch (err) {
      setTemplateMsg('Fehler: ' + err.message);
    } finally {
      setTemplateBusy(false);
    }
  };

  // Liste der Member, die noch nicht in der gewählten Abteilung sind
  const availableMembersFor = (deptId) => {
    const inDept = new Set((memberDataByDept[deptId] || []).map(m => m.userId));
    return members.filter(m => !inDept.has(m.userId));
  };

  if (loading) return <p>Lädt…</p>;

  return (
    <div className="departments-panel">
      <div className="departments-toolbar">
        {canEdit && (
          <form onSubmit={createTop} className="departments-create-form">
            <input
              className="text-input"
              placeholder="Neue Abteilung (z.B. IT, Marketing)"
              value={creatingTopName}
              onChange={e => setCreatingTopName(e.target.value)}
            />
            <button type="submit" className="btn-sm-primary" disabled={!creatingTopName.trim()}>
              + Abteilung
            </button>
          </form>
        )}
        {canEdit && (
          <div className="departments-template-actions">
            <button className="link-btn" onClick={openExport}>📤 Exportieren</button>
            <button className="link-btn" onClick={openImport}>📥 Importieren</button>
          </div>
        )}
      </div>
      {error && <div className="form-error">{error}</div>}

      {topLevels.length === 0 ? (
        <p className="section-hint">Noch keine Abteilungen. Lege oben eine an, oder importiere eine Vorlage.</p>
      ) : (
        <ul className="department-tree">
          {topLevels.map(top => {
            const kids = childrenOf(top._id);
            const isOpen = expandedIds.has(top._id);
            const topMembers = memberDataByDept[top._id] || [];
            return (
              <li key={top._id} className="department-tree-node">
                <div className="department-tree-row">
                  <button className="department-toggle" onClick={() => toggleExpand(top._id)}>
                    {isOpen ? '▾' : '▸'}
                  </button>
                  <span className="department-icon">🏛</span>
                  {editingId === top._id ? (
                    <input
                      className="text-input"
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onBlur={() => submitRename(top._id)}
                      onKeyDown={e => e.key === 'Enter' && submitRename(top._id)}
                      autoFocus
                    />
                  ) : (
                    <span className="department-name" onDoubleClick={() => canEdit && (setEditingId(top._id), setEditingName(top.name))}>
                      {top.name}
                    </span>
                  )}
                  <span className="department-meta">
                    {top.memberCount} Mitglied(er) · {kids.length} Unter-Abt.
                  </span>
                  {canEdit && (
                    <div className="department-actions">
                      <button
                        className="link-btn"
                        onClick={() => { setCreatingChildFor(top._id); setCreatingChildName(''); if (!isOpen) toggleExpand(top._id); }}
                        title="Unter-Abteilung anlegen"
                      >+ Unter</button>
                      <button className="link-btn" onClick={() => { setEditingId(top._id); setEditingName(top.name); }}>✏️</button>
                      <button className="link-btn danger" onClick={() => deleteDept(top._id, top.name)}>🗑️</button>
                    </div>
                  )}
                </div>

                {isOpen && (
                  <div className="department-children">
                    {/* Mitglieder der Top-Abteilung */}
                    <DepartmentMembersBlock
                      deptId={top._id}
                      deptMembers={topMembers}
                      canEdit={canEdit}
                      isPickerOpen={memberPickerFor === top._id}
                      pickerUserId={pickerUserId}
                      pickerRole={pickerRole}
                      onOpenPicker={() => { setMemberPickerFor(top._id); setPickerUserId(''); setPickerRole('member'); }}
                      onClosePicker={() => setMemberPickerFor(null)}
                      onPickerUserChange={setPickerUserId}
                      onPickerRoleChange={setPickerRole}
                      availableMembers={availableMembersFor(top._id)}
                      onAdd={() => addMember(top._id)}
                      onRoleChange={(uid, r) => changeMemberRole(top._id, uid, r)}
                      onRemove={(uid) => removeMember(top._id, uid)}
                      label="Direkte Mitglieder (z.B. Abteilungsleitung)"
                    />

                    {/* Unter-Abteilungen */}
                    {kids.length > 0 && (
                      <ul className="department-subtree">
                        {kids.map(child => {
                          const childOpen = expandedIds.has(child._id);
                          const childMembers = memberDataByDept[child._id] || [];
                          return (
                            <li key={child._id} className="department-tree-node sub">
                              <div className="department-tree-row">
                                <button className="department-toggle" onClick={() => toggleExpand(child._id)}>
                                  {childOpen ? '▾' : '▸'}
                                </button>
                                <span className="department-icon sub">└─</span>
                                {editingId === child._id ? (
                                  <input
                                    className="text-input"
                                    value={editingName}
                                    onChange={e => setEditingName(e.target.value)}
                                    onBlur={() => submitRename(child._id)}
                                    onKeyDown={e => e.key === 'Enter' && submitRename(child._id)}
                                    autoFocus
                                  />
                                ) : (
                                  <span className="department-name">{child.name}</span>
                                )}
                                <span className="department-meta">{child.memberCount} Mitglied(er)</span>
                                {canEdit && (
                                  <div className="department-actions">
                                    <button className="link-btn" onClick={() => { setEditingId(child._id); setEditingName(child.name); }}>✏️</button>
                                    <button className="link-btn danger" onClick={() => deleteDept(child._id, child.name)}>🗑️</button>
                                  </div>
                                )}
                              </div>
                              {childOpen && (
                                <div className="department-children">
                                  <DepartmentMembersBlock
                                    deptId={child._id}
                                    deptMembers={childMembers}
                                    canEdit={canEdit}
                                    isPickerOpen={memberPickerFor === child._id}
                                    pickerUserId={pickerUserId}
                                    pickerRole={pickerRole}
                                    onOpenPicker={() => { setMemberPickerFor(child._id); setPickerUserId(''); setPickerRole('member'); }}
                                    onClosePicker={() => setMemberPickerFor(null)}
                                    onPickerUserChange={setPickerUserId}
                                    onPickerRoleChange={setPickerRole}
                                    availableMembers={availableMembersFor(child._id)}
                                    onAdd={() => addMember(child._id)}
                                    onRoleChange={(uid, r) => changeMemberRole(child._id, uid, r)}
                                    onRemove={(uid) => removeMember(child._id, uid)}
                                    label="Mitglieder"
                                  />
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    {/* Neue Unter-Abteilung anlegen */}
                    {canEdit && creatingChildFor === top._id && (
                      <form
                        className="department-child-form"
                        onSubmit={e => { e.preventDefault(); createChild(top._id); }}
                      >
                        <input
                          className="text-input"
                          placeholder="Name der Unter-Abteilung"
                          value={creatingChildName}
                          onChange={e => setCreatingChildName(e.target.value)}
                          autoFocus
                        />
                        <button type="submit" className="btn-sm-primary" disabled={!creatingChildName.trim()}>Anlegen</button>
                        <button type="button" className="link-btn" onClick={() => { setCreatingChildFor(null); setCreatingChildName(''); }}>Abbrechen</button>
                      </form>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Template-Modal */}
      {showTemplate && (
        <div className="modal-backdrop" onClick={() => setShowTemplate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Abteilungs-Vorlage</h3>
              <button className="icon-btn" onClick={() => setShowTemplate(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p className="section-hint">
                Kopiere das JSON, um es in einem anderen Mandanten zu importieren — oder füge ein zuvor
                exportiertes JSON hier ein und klicke „Importieren".
              </p>
              <textarea
                className="text-input"
                style={{ minHeight: 240, fontFamily: 'monospace', fontSize: 12 }}
                value={templateText}
                onChange={e => setTemplateText(e.target.value)}
              />
              {templateMsg && <div className="section-hint" style={{ marginTop: 8 }}>{templateMsg}</div>}
            </div>
            <div className="modal-actions">
              {canEdit && (
                <button className="btn-sm-primary" onClick={submitImport} disabled={templateBusy || !templateText.trim()}>
                  {templateBusy ? '…' : 'Importieren'}
                </button>
              )}
              <button className="btn-sm-cancel" onClick={() => setShowTemplate(false)}>Schließen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Block für die Mitgliederliste einer Abteilung — wiederverwendet für Top + Sub
function DepartmentMembersBlock({
  deptMembers, canEdit, label,
  isPickerOpen, pickerUserId, pickerRole,
  onOpenPicker, onClosePicker, onPickerUserChange, onPickerRoleChange,
  availableMembers, onAdd, onRoleChange, onRemove
}) {
  return (
    <div className="department-members">
      <div className="department-members-head">
        <span className="department-members-label">{label}: {deptMembers.length}</span>
        {canEdit && !isPickerOpen && (
          <button className="link-btn" onClick={onOpenPicker}>+ Mitglied</button>
        )}
      </div>

      {isPickerOpen && (
        <div className="department-member-picker">
          {availableMembers.length === 0 ? (
            <span className="section-hint" style={{ margin: 0 }}>Alle Mandanten-Mitglieder sind bereits in dieser Abteilung.</span>
          ) : (
            <>
              <select value={pickerUserId} onChange={e => onPickerUserChange(e.target.value)}>
                <option value="">— Mitglied auswählen —</option>
                {availableMembers.map(m => (
                  <option key={m.userId} value={m.userId}>{m.username} ({m.email})</option>
                ))}
              </select>
              <select value={pickerRole} onChange={e => onPickerRoleChange(e.target.value)}>
                <option value="member">Mitglied</option>
                <option value="lead">Lead (Leitung)</option>
              </select>
              <button className="btn-sm-primary" onClick={onAdd} disabled={!pickerUserId}>Hinzufügen</button>
            </>
          )}
          <button className="link-btn" onClick={onClosePicker}>Abbrechen</button>
        </div>
      )}

      {deptMembers.length === 0 ? (
        <p className="section-hint" style={{ margin: '4px 0' }}>Keine Mitglieder.</p>
      ) : (
        <ul className="department-member-list">
          {deptMembers.map(m => (
            <li key={m.userId} className="department-member-item">
              <span className="department-member-name">{m.username}</span>
              <span className="department-member-email">{m.email}</span>
              {canEdit ? (
                <select value={m.role} onChange={e => onRoleChange(m.userId, e.target.value)}>
                  <option value="member">Mitglied</option>
                  <option value="lead">Lead</option>
                </select>
              ) : (
                <span className={`tenant-role-badge role-${m.role === 'lead' ? 'admin' : 'member'}`}>
                  {m.role === 'lead' ? 'Lead' : 'Mitglied'}
                </span>
              )}
              {canEdit && (
                <button className="link-btn danger" onClick={() => onRemove(m.userId)}>Entfernen</button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
